import {Box, Button, Center, Container, HStack, Spinner, Text} from '@chakra-ui/react';
import {colors, useCartContext} from 'components/providers';
import {useRouter} from 'next/router';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import ImageUI from 'ui/ImageUI';

import {useDesktop, useTranslation} from '../../../../hooks';
import {IMetroSearchResponse} from '../../../metro/types';
import {NotFoundBanner} from '../../../pages/search/not-found-banner';
import {MetroSearchResultItem} from './components';

interface Props {
  data?: IMetroSearchResponse;
  onProductClick?: (product: IProduct) => void;
  topPadding?: string;
  isLoading?: boolean;
  value: string;
  provider?: IRestaurantResponse;
  inputRef?: React.RefObject<HTMLInputElement>;
  handleSearch?: (e: React.FormEvent) => void;
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
}

export const MetroSearchResults: FC<Props> = ({
  data,
  onProductClick,
  topPadding,
  isLoading,
  value,
  provider,
  inputRef,
  handleSearch,
  setShowSuggestions,
}) => {
  const {push} = useRouter();
  const {t, getProductPlural} = useTranslation();
  const isDesktop = useDesktop();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const onCategoryClick = (slug: string) => {
    push(`/categories/${slug}`);
    setShowSuggestions(false);
  };

  const updateScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -400,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 400,
        behavior: 'smooth',
      });
    }
  }, []);

  const categories = useMemo(() => data?.categories ?? [], [data?.categories]);
  const products = useMemo(() => data?.products ?? [], [data?.products]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [categories, updateScrollButtons]);

  const noResults = !isLoading && categories?.length === 0 && products?.length === 0 && value !== '';

  const {cartItems} = useCartContext();

  const cartMap = useMemo(() => {
    const m = new Map<number, ICartItem>();
    cartItems.forEach((c) => m.set(c.productId, c));
    return m;
  }, [cartItems]);

  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
  }, []);

  if (!isLoading && value === '') {
    return null;
  }

  const loadingHeight = isDesktop ? '70vh' : '50vh';

  const isMobile = !isDesktop;
  const containerRight: any = isMobile ? '0' : isDesktop ? undefined : '-4';
  const productsMobileHeight = '100%';
  const pluralProduct = getProductPlural(data?.products_count || 0).toLowerCase();

  const containerProps: any = {
    variant: 'white-block',
    position: isMobile ? 'fixed' : 'absolute',
    border: '1px solid',
    borderRadius: isMobile ? 0 : undefined,
    left: isMobile ? '0' : undefined,
    right: containerRight,
    width: isDesktop ? '100%' : '100vw',
    borderColor: colors.borderGray,
    boxShadow: isMobile ? 'none' : '10px 10px 15px -4px rgba(181,181,181,1)',
    mt: isMobile ? 0 : 2,
    top: topPadding ? topPadding : '46px',
    h: isMobile ? '100%' : isLoading ? loadingHeight : 'auto',
    maxW: isDesktop ? '100%' : 'none',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    role: 'alertdialog',
    scrollBehavior: 'none',
    padding: isMobile ? '0' : undefined,
    paddingBottom: '10',
  };

  return (
    <Container
      visibility={mounted ? 'visible' : 'hidden'}
      {...containerProps}
      onTouchStart={() => inputRef?.current?.blur()}
    >
      {isLoading ? (
        <Center h="100%">
          <Spinner size="xl" color={colors.pink} thickness="4px" />
        </Center>
      ) : (
        <>
          <Box
            paddingY={4}
            paddingX={6}
            mr="-4"
            ml="-4"
            mb="20px"
            overflowY="auto"
            overflowX="hidden"
            sx={{
              scrollbarGutter: 'stable',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: colors.borderGray,
                borderRadius: '8px',
              },
            }}
          >
            {categories.length ? (
              <Box borderBottom="3px solid" borderColor={colors.borderGray} pb="20px" mb="20px">
                <Text fontSize="14px" fontWeight="600" mb="5px">
                  {t('CATEGORIES')}
                </Text>
                <Box position="relative" mt="20px" marginX="-4">
                  {canScrollLeft && (
                    <Box
                      position="absolute"
                      // left="-3"
                      left="1"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex="2"
                      cursor="pointer"
                      onClick={scrollLeft}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="32px"
                      h="32px"
                      bg="white"
                      borderRadius="50%"
                      boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                      _hover={{
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      }}
                    >
                      <ImageUI
                        src="/arrow-right-icon.svg"
                        alt="Scroll left"
                        w="16px"
                        h="16px"
                        transform="rotate(180deg)"
                      />
                    </Box>
                  )}

                  {canScrollRight && (
                    <Box
                      position="absolute"
                      right="1"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex="2"
                      cursor="pointer"
                      onClick={scrollRight}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="32px"
                      h="32px"
                      bg="white"
                      borderRadius="50%"
                      boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                      _hover={{
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      }}
                    >
                      <ImageUI src="/arrow-right-icon.svg" alt="Scroll right" w="16px" h="16px" />
                    </Box>
                  )}

                  <HStack
                    ref={scrollContainerRef}
                    onScroll={() => {
                      updateScrollButtons();
                    }}
                    overflowX="auto"
                    spacing="15px"
                    sx={{
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                    }}
                  >
                    {categories.map((item, index) => (
                      <Box
                        key={item.id}
                        bgColor={colors.lightGray}
                        borderRadius="20px"
                        p="10px"
                        cursor="pointer"
                        onClick={() => onCategoryClick(item.slug)}
                        ml={index === 0 ? '4' : undefined}
                      >
                        <Text fontSize="14px" fontWeight="500" whiteSpace="nowrap">
                          {item.name}
                        </Text>
                      </Box>
                    ))}
                  </HStack>
                </Box>
              </Box>
            ) : null}
            {products.length ? (
              <Text fontSize="14px" fontWeight="600" mb="5px">
                {t('PRODUCTS')}
              </Text>
            ) : null}
            <Box maxH={isMobile ? productsMobileHeight : '60vh'} mt="20px" pt={1}>
              {products.map((item) => {
                const cartItem = cartMap.get(item.id);
                return (
                  <MetroSearchResultItem
                    key={item.id}
                    onProductClick={onProductClick}
                    item={item}
                    provider={provider}
                    quantity={cartItem?.quantity ?? 0}
                    cartItemId={cartItem?.id}
                  />
                );
              })}
            </Box>
          </Box>
          {products.length > 0 && data?.products_count && data?.products_count > 0 && (
            <Button
              variant="outline"
              onClick={handleSearch}
              backgroundColor={colors.white}
              zIndex={1300}
              w="full"
              position={isMobile ? 'sticky' : undefined}
              mt={isMobile ? 'auto' : undefined}
              bottom={isMobile ? 0 : undefined}
            >
              {`${t('VIEW_ALL')} ${data?.products_count} ${pluralProduct}`}
            </Button>
          )}
        </>
      )}
      {noResults && <NotFoundBanner icon="/icons/search-not-found.svg" title={t('SEARCH_NOT_FOUND')} />}
    </Container>
  );
};
